import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import { GraduationCap, Lock, Users, UserRoundPlus, Apple, MapPin, Pin } from 'lucide-react';
import { ClassCardData } from '../../businessLogicService/getClassCardData';
import { externalSyncMessage } from '../../models/Class';
import { AttentionOverlay } from '../common';
import ClassesRosterCardMenu, { ClassMenuAction } from './ClassesRosterCardMenu';
import ClassBadge from '../ClassIcon/ClassBadge';
import { useSideNavActive } from '../LayoutWrapper/SideNavLayoutContext';

interface Props {
  data: ClassCardData;
  isAdmin?: boolean;
  onMenuAction?: (action: ClassMenuAction, classId: string) => void;
  /** Radiates a pulse + shows the "start here" overlay to nudge the teacher to this card. */
  highlighted?: boolean;
  icon?: string | null;
  workingIn?: boolean;
}

const ClassesRosterCard: FC<Props> = ({
  data,
  isAdmin = false,
  onMenuAction,
  highlighted = false,
  icon = null,
  workingIn = false,
}) => {
  // The overlay dismisses on its own X; the pulse keeps looping until the page is left.
  const [showOverlay, setShowOverlay] = useState(highlighted);
  const sideNavActive = useSideNavActive();
  const { id, name, section, gradeLabel, studentCount, hasCoTeacher, isClever, ownerName, siteName } = data;

  // The admin + Clever footer (site + co-teacher + Clever) is the crowded case: there the
  // co-teacher label collapses to icon-only via a container query, but reappears when the
  // card is wide enough. Teacher / admin-without-Clever cards always keep the label.
  const collapsibleCoTeacher = isAdmin && hasCoTeacher && isClever;

  const coTeacher = hasCoTeacher && (
    <span
      className={`nc_class-card__pill flex align-items-center gap-xs${
        collapsibleCoTeacher ? ' nc_class-card__pill--collapsible' : ''
      }`}
      title="Co-teacher"
    >
      <UserRoundPlus size={14} aria-hidden />
      <span className="nc_class-card__pill-label">Co-teacher</span>
    </span>
  );

  const clever = isClever && (
    <span
      className="flex align-items-center gap-xs nc_clever-indicator"
      data-pr-tooltip={externalSyncMessage}
      data-pr-position="top"
    >
      <Tooltip target=".nc_clever-indicator" />
      <Lock size={14} aria-hidden />
      <span>Clever</span>
    </span>
  );

  return (
    <div className={`nc_class-card flex flex-column h-full${highlighted ? ' nc_pulse-attention' : ''}`}>
      {workingIn && (
        <span className="nc_class-card__working-in">
          <Pin size={12} aria-hidden="true" />
          Working in
        </span>
      )}
      <Link
        to={`/classes-rosters?classId=${encodeURIComponent(id)}`}
        className="tw-absolute tw-inset-0"
        aria-label={`Open ${name}`}
      />

      {showOverlay && (
        <AttentionOverlay
          title="Open a class to see its students"
          message="You can check everyone's on the list and fix a spelling or add a student"
          onClose={() => setShowOverlay(false)}
        />
      )}
      <div className="flex justify-content-between align-items-start gap-sm">
        {sideNavActive ? (
          <div className="flex align-items-center gap-sm min-w-0 flex-grow-1">
            <ClassBadge variant="class" icon={icon} />
            <h4 className="m-0 nc_class-card__title">{section ? `${name} – ${section}` : name}</h4>
          </div>
        ) : (
          <div className="flex flex-column">
            <h4 className="m-0">{name}</h4>
            {section && <span>{section}</span>}
          </div>
        )}
        <ClassesRosterCardMenu name={name} isClever={isClever} onAction={action => onMenuAction?.(action, id)} />
      </div>

      {isAdmin && ownerName && (
        <span className="flex align-items-center gap-xs mt-2">
          <Apple size={16} color="#9C9B9B" aria-hidden />
          <span className="font-bold">{ownerName}</span>
        </span>
      )}

      <div className="flex align-items-center gap-lg mt-3">
        {gradeLabel && (
          <span className="flex align-items-center gap-xs">
            <GraduationCap size={16} color="#9C9B9B" aria-hidden />
            <span className="font-semibold">{gradeLabel}</span>
          </span>
        )}
        <span className="flex align-items-center gap-xs">
          <Users size={16} color="#9C9B9B" aria-hidden />
          <span>
            <span className="font-semibold">{studentCount}</span> students
          </span>
        </span>
      </div>

      {isAdmin ? (
        <>
          <hr className="nc_class-card__divider w-full my-3" />
          <div className="flex justify-content-between align-items-center gap-md">
            <span className="nc_class-card__site flex align-items-center gap-xs">
              <MapPin size={14} color="#9C9B9B" aria-hidden />
              <span className="nc_class-card__site-name">{siteName}</span>
            </span>
            <div className="flex align-items-center gap-md flex-shrink-0">
              {coTeacher}
              {clever}
            </div>
          </div>
        </>
      ) : (
        (hasCoTeacher || isClever) && (
          <>
            <hr className="nc_class-card__divider w-full my-3" />
            <div className="flex justify-content-between align-items-center">
              {coTeacher || <span />}
              {clever}
            </div>
          </>
        )
      )}
    </div>
  );
};

export default ClassesRosterCard;
